import { useCallback } from 'react';

import { ErrorName } from 'errors';

import { useStores } from 'mobx/hooks/useStores';

import Ticket from 'models/Ticket';

import { useTicketActionCallbacks } from 'components/Ticket/TicketsContainers/useTicketActionCallbacks';
import useTicketOverviewContext from 'components/Ticket/TicketsContainers/useTicketOverviewContext';

export interface CloseTicketsData {
  tickets: Ticket[];
  patientId?: number;
}

export interface UseTicketCloseReturn {
  (closeTicketsData: CloseTicketsData, navigationPath?: string): Promise<void>;
}

export const useTicketClose = (): UseTicketCloseReturn => {
  const { tasksStore } = useStores();
  const {
    openDisconnectDraftPopup,
    openResolveOrCloseTicketWithMessageForTheFirstTimeDialog,
    openUndeliveredMessageToPatientDialog,
    openHidePatientDialog,
    openMergePatientsModal
  } = useTicketOverviewContext();
  const { onClose } = useTicketActionCallbacks();

  return useCallback(
    async function closeTickets(closeTicketsData: CloseTicketsData, navigationPath?: string) {
      const { tickets } = closeTicketsData;
      const ticketIds = tickets.map((ticket) => ticket.id);

      try {
        await tasksStore.closeTasksTickets(tickets);
        onClose && onClose(ticketIds, navigationPath);
      } catch (error: any) {
        if (error.name === ErrorName.ResolveClosedTicket) {
          onClose && onClose(ticketIds);
          return undefined;
        }

        if (error.name === ErrorName.TicketConnectedToDraft) {
          openDisconnectDraftPopup({
            ticketIds,
            callback: async () => {
              await closeTickets(closeTicketsData);
            },
            action: 'close'
          });
          return;
        }

        if (error.name === ErrorName.ResolvingFailedOutboundMessages) {
          openUndeliveredMessageToPatientDialog(closeTicketsData, 'close');
          return;
        }

        if (error.name === ErrorName.PatientCannotReplyAfterTicketResolve) {
          openResolveOrCloseTicketWithMessageForTheFirstTimeDialog(closeTicketsData, 'close');
          return;
        }

        if (error.name === ErrorName.DuplicateEmrPatientFound) {
          openMergePatientsModal(
            closeTicketsData,
            error.httpFailure?.serverData?.duplicatePatientData,
            'close'
          );
          return;
        }

        if (
          error.name === ErrorName.NoDuplicateEmrPatientFound ||
          error.name === ErrorName.MultipleDuplicateEmrPatientFound
        ) {
          openHidePatientDialog(closeTicketsData, 'close');
          return;
        }

        throw error;
      }
    },
    [
      tasksStore,
      onClose,
      openDisconnectDraftPopup,
      openResolveOrCloseTicketWithMessageForTheFirstTimeDialog,
      openUndeliveredMessageToPatientDialog,
      openHidePatientDialog,
      openMergePatientsModal
    ]
  );
};
